<template>
  <div class="product-mobile" :style="shellStyle" dir="rtl">
    <header class="product-mobile-header">
      <button
        type="button"
        class="product-mobile-header__icon product-mobile-header__back"
        :aria-label="$t('back')"
        @click="onBack"
      >
        <v-icon size="24" color="#37474F">mdi-arrow-left</v-icon>
      </button>
      <h1 class="product-mobile-header__title">
        {{ $t("product") }} / {{ $t("service") }}
      </h1>
      <button
        v-if="!p.formType"
        type="button"
        class="product-mobile-header__icon product-mobile-header__list"
        :aria-label="p.type === 'product' ? $t('productList') : $t('serviceList')"
        @click="p.goTo(p.type === 'product' ? 'ProductList' : 'ServiceList')"
      >
        <v-icon size="22" color="#37474F">mdi-format-list-numbered</v-icon>
      </button>
      <span v-else class="product-mobile-header__icon product-mobile-header__list" />
    </header>

    <v-form
      v-if="p.render"
      ref="form"
      v-model="mobileValid"
      lazy-validation
      class="product-mobile__form"
      @submit.prevent
    >
      <section
        v-if="showAdvancedSegments"
        class="product-mobile-advanced product-mobile-advanced--top"
        dir="rtl"
      >
        <div class="product-mobile-advanced__chips">
          <button
            type="button"
            class="product-mobile-advanced__chip"
            :class="{ 'product-mobile-advanced__chip--on': mobileTab === 'main' }"
            @click="setMobileTab('main')"
          >
            <v-icon size="16" :color="mobileTab === 'main' ? '#fff' : '#5B7C99'">mdi-package-variant-closed</v-icon>
            <span>{{ $t("product") }}</span>
          </button>
          <button
            type="button"
            class="product-mobile-advanced__chip"
            :class="{ 'product-mobile-advanced__chip--on': mobileTab === 'pricing' }"
            @click="setMobileTab('pricing')"
          >
            <v-icon size="16" :color="mobileTab === 'pricing' ? '#fff' : '#5B7C99'">mdi-cash-multiple</v-icon>
            <span>{{ $t("pricing") }} {{ $t("advance") }}</span>
          </button>
          <button
            type="button"
            class="product-mobile-advanced__chip"
            :class="{ 'product-mobile-advanced__chip--on': mobileTab === 'integrator' }"
            @click="setMobileTab('integrator')"
          >
            <v-icon size="16" :color="mobileTab === 'integrator' ? '#fff' : '#5B7C99'">mdi-transit-connection-variant</v-icon>
            <span>{{ $t("Integrator") }}</span>
          </button>
          <button
            v-if="$store.state.shopSystem"
            type="button"
            class="product-mobile-advanced__chip"
            :class="{ 'product-mobile-advanced__chip--on': mobileTab === 'shop' }"
            @click="setMobileTab('shop')"
          >
            <v-icon size="16" :color="mobileTab === 'shop' ? '#fff' : '#5B7C99'">mdi-storefront-outline</v-icon>
            <span>{{ $t("shop") }}</span>
          </button>
        </div>
      </section>

      <div class="product-mobile__scroll">
        <div v-show="mobileTab === 'main'">
        <!-- Type (right, compact) + product flags (left) -->
        <div
          v-if="p.showTypeSelector || p.type === 'product'"
          class="product-mobile-toolbar"
        >
          <div v-if="p.showTypeSelector" class="product-mobile-type product-mobile-type--compact">
            <button
              type="button"
              class="product-mobile-type__item"
              :class="{ 'product-mobile-type__item--active': p.type === 'product' }"
              :disabled="!!p.id"
              @click="p.type = 'product'"
            >
              {{ $t("product") }}
            </button>
            <button
              type="button"
              class="product-mobile-type__item"
              :class="{ 'product-mobile-type__item--active': p.type === 'service' }"
              :disabled="!!p.id"
              @click="p.type = 'service'"
            >
              {{ $t("service") }}
            </button>
          </div>
          <div v-if="p.type === 'product'" class="product-mobile-flags">
            <button
              type="button"
              class="product-mobile-flag"
              :class="{ 'product-mobile-flag--on': p.isVariableProduct }"
              :disabled="!!p.id || p.wasVariableProduct"
              @click="toggleVariableProduct"
            >
              {{ $t("variant-product") }}
            </button>
            <button
              type="button"
              class="product-mobile-flag"
              :class="{ 'product-mobile-flag--on': p.has_serial === 'active' }"
              @click="toggleProductSerial"
            >
              {{ $t("product-serial") }}
            </button>
          </div>
        </div>

        <!-- Hero: image + name + code + status -->
        <section class="product-mobile-card product-mobile-hero">
          <div class="product-mobile-hero__body">
            <div class="product-mobile-hero__row">
              <div class="product-mobile-hero__avatar">
                <file-picker
                  ref="refFilePicker"
                  v-model="p.imagePicker"
                  :label="$t('uploadImage')"
                  :avatar="true"
                  :multiple="false"
                  extensions="image/png, image/gif, image/jpeg"
                  type-prop="productAvatar"
                  :val="p.imagePickerVal ? p.imagePickerVal : null"
                />
              </div>
              <div class="product-mobile-hero__main">
                <v-text-field
                  v-model="p.name"
                  :label="$t('name')"
                  :rules="[(v) => !!v || $t('is-required')]"
                  dense
                  outlined
                  hide-details="auto"
                  class="product-mobile-hero__name ma-0"
                  clearable
                />
                <button
                  type="button"
                  class="product-mobile-hero__code-field"
                  @click="openTextSheet('code', $t('code'))"
                >
                  <span class="product-mobile-hero__code-label">{{ $t("code") }}</span>
                  <span
                    class="product-mobile-hero__code-value"
                    :class="{ 'product-mobile-hero__code-value--empty': !p.code }"
                  >{{ p.code || "—" }}</span>
                  <v-icon size="16" color="#90A4AE">mdi-pencil-outline</v-icon>
                </button>
              </div>
            </div>

            <div class="product-mobile-hero__status-row">
              <span class="product-mobile-hero__status-label">
                {{ p.status === "active" ? $t("active") : $t("deactive") }}
              </span>
              <v-switch
                class="mt-0 pt-0 product-mobile-hero__status"
                dense
                inset
                hide-details
                :input-value="p.status === 'active'"
                color="#5B7C99"
                @change="onStatusChange"
              />
            </div>
          </div>
        </section>

        <!-- Specs: inline fields (form feel, not menu) -->
        <section class="product-mobile-card product-mobile-summary" dir="rtl">
          <div
            class="product-mobile-field product-mobile-field--select"
            @click="openUnitPicker"
          >
            <span class="product-mobile-field__label">{{ $t("unit") }}</span>
            <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
              <span :class="{ 'product-mobile-field__placeholder': !unitText }">
                {{ unitText || "—" }}
              </span>
            </button>
          </div>

          <div
            v-if="p.type === 'product' && !p.isVariableProduct"
            class="product-mobile-field"
          >
            <label class="product-mobile-field__label" for="pm-barcode">{{ $t("barcode") }}</label>
            <input
              id="pm-barcode"
              v-model="p.barcode"
              type="text"
              class="product-mobile-field__control product-mobile-field__input"
              placeholder="—"
              autocomplete="off"
            />
          </div>

          <div
            v-if="p.type === 'product'"
            class="product-mobile-field product-mobile-field--select"
            @click="openKindPicker"
          >
            <span class="product-mobile-field__label">{{ $t("productKind") }}</span>
            <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
              <span :class="{ 'product-mobile-field__placeholder': !kindText }">
                {{ kindText || "—" }}
              </span>
            </button>
          </div>

          <template v-if="summaryMoreOpen">
            <div class="product-mobile-field">
              <label class="product-mobile-field__label" for="pm-priority">{{ $t("displayPriority") }}</label>
              <input
                id="pm-priority"
                v-model="p.displayPriority"
                type="number"
                inputmode="numeric"
                class="product-mobile-field__control product-mobile-field__input"
                placeholder="—"
              />
            </div>

            <div v-if="!p.isVariableProduct" class="product-mobile-field">
              <label class="product-mobile-field__label" for="pm-custom-code">{{ $t("Custom financial ID") }}</label>
              <input
                id="pm-custom-code"
                v-model="p.custom_code"
                type="text"
                class="product-mobile-field__control product-mobile-field__input"
                placeholder="—"
                autocomplete="off"
              />
            </div>

            <div class="product-mobile-field product-mobile-field--area">
              <label class="product-mobile-field__label" for="pm-description">{{ $t("description") }}</label>
              <textarea
                id="pm-description"
                v-model="p.content"
                class="product-mobile-field__control product-mobile-field__textarea"
                rows="5"
                placeholder="—"
              />
            </div>
          </template>

          <button
            type="button"
            class="product-mobile-summary__more"
            @click="summaryMoreOpen = !summaryMoreOpen"
          >
            {{ summaryMoreOpen ? "کمتر" : $t("more") }}
            <v-icon size="18" color="#5B7C99">
              {{ summaryMoreOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
            </v-icon>
          </button>
        </section>

        <!-- Desktop expansion panels → same titles, each opens a bottom sheet -->
        <section class="product-mobile-card product-mobile-sections">
          <button type="button" class="product-mobile-sections__row" @click="openSection('tax')">
            <v-icon class="product-mobile-sections__icon" size="22" color="#5B7C99">mdi-calculator-variant-outline</v-icon>
            <div class="product-mobile-sections__copy">
              <div class="product-mobile-sections__title">{{ $t("tax&priceing") }}</div>
              <div class="product-mobile-sections__hint">
                {{ $t("taxisProduct") }} , {{ $t("taxPercent") }} ,
                {{ $t("fixSellPrice") }} , {{ $t("discount") }} , ...
              </div>
            </div>
            <v-icon size="20" color="#B0BEC5">mdi-chevron-down</v-icon>
          </button>

          <button
            v-if="p.type === 'product' && !p.isVariableProduct"
            type="button"
            class="product-mobile-sections__row"
            @click="openSection('barcodes')"
          >
            <v-icon class="product-mobile-sections__icon" size="22" color="#5B7C99">mdi-barcode</v-icon>
            <div class="product-mobile-sections__copy">
              <div class="product-mobile-sections__title">{{ $t("otherBarcodes") }}</div>
              <div class="product-mobile-sections__hint">{{ $t("otherBarcodesDescrible") }}</div>
            </div>
            <v-icon size="20" color="#B0BEC5">mdi-chevron-down</v-icon>
          </button>

          <button type="button" class="product-mobile-sections__row" @click="openSection('grouping')">
            <v-icon class="product-mobile-sections__icon" size="22" color="#5B7C99">mdi-tag-outline</v-icon>
            <div class="product-mobile-sections__copy">
              <div class="product-mobile-sections__title">{{ $t("grouping,tags") }}</div>
              <div class="product-mobile-sections__hint">
                {{ $t("tag") }} , {{ $t("group") }} , {{ $t("category") }} , ...
              </div>
            </div>
            <v-icon size="20" color="#B0BEC5">mdi-chevron-down</v-icon>
          </button>

          <button
            v-if="p.type === 'product'"
            type="button"
            class="product-mobile-sections__row"
            @click="openSection('dimensions')"
          >
            <v-icon class="product-mobile-sections__icon" size="22" color="#5B7C99">mdi-cube-outline</v-icon>
            <div class="product-mobile-sections__copy">
              <div class="product-mobile-sections__title">{{ $t("dimensions") }}</div>
              <div class="product-mobile-sections__hint">
                {{ $t("weight") }} , {{ $t("length") }} , {{ $t("width") }} ,
                {{ $t("height") }}
              </div>
            </div>
            <v-icon size="20" color="#B0BEC5">mdi-chevron-down</v-icon>
          </button>

          <button
            v-if="$store.state.isMainYear && p.type === 'product' && !p.isVariableProduct"
            type="button"
            class="product-mobile-sections__row"
            @click="openSection('stock')"
          >
            <v-icon class="product-mobile-sections__icon" size="22" color="#5B7C99">mdi-package-variant-closed</v-icon>
            <div class="product-mobile-sections__copy">
              <div class="product-mobile-sections__title">{{ $t("initial-stock") }}</div>
              <div class="product-mobile-sections__hint">{{ $t("initialStockInOtherEntrepot") }}</div>
            </div>
            <v-icon size="20" color="#B0BEC5">mdi-chevron-down</v-icon>
          </button>

          <button
            v-if="p.type === 'product'"
            type="button"
            class="product-mobile-sections__row"
            @click="openSection('units')"
          >
            <v-icon class="product-mobile-sections__icon" size="22" color="#5B7C99">mdi-ruler</v-icon>
            <div class="product-mobile-sections__copy">
              <div class="product-mobile-sections__title">{{ $t("otherUnits") }}</div>
              <div class="product-mobile-sections__hint">{{ $t("initialStockInOtherEntrepot") }}</div>
            </div>
            <v-icon size="20" color="#B0BEC5">mdi-chevron-down</v-icon>
          </button>

          <button
            v-if="hasCustomFields"
            type="button"
            class="product-mobile-sections__row"
            @click="openSection('customFields')"
          >
            <v-icon class="product-mobile-sections__icon" size="22" color="#5B7C99">mdi-form-textbox</v-icon>
            <div class="product-mobile-sections__copy">
              <div class="product-mobile-sections__title">{{ $t("customFields") }}</div>
              <div class="product-mobile-sections__hint">{{ $t("customeFeildDescrible") }}</div>
            </div>
            <v-icon size="20" color="#B0BEC5">mdi-chevron-down</v-icon>
          </button>

          <button type="button" class="product-mobile-sections__row" @click="openSection('otherSetting')">
            <v-icon class="product-mobile-sections__icon" size="22" color="#5B7C99">mdi-cog-outline</v-icon>
            <div class="product-mobile-sections__copy">
              <div class="product-mobile-sections__title">{{ $t("otherSetting") }}</div>
              <div class="product-mobile-sections__hint">{{ $t("otherSettingDescrible") }}</div>
            </div>
            <v-icon size="20" color="#B0BEC5">mdi-chevron-down</v-icon>
          </button>

          <button type="button" class="product-mobile-sections__row" @click="openSection('images')">
            <v-icon class="product-mobile-sections__icon" size="22" color="#5B7C99">mdi-image-multiple-outline</v-icon>
            <div class="product-mobile-sections__copy">
              <div class="product-mobile-sections__title">{{ $t("image-gallery") }}</div>
              <div class="product-mobile-sections__hint">{{ $t("upload your images") }}</div>
            </div>
            <v-icon size="20" color="#B0BEC5">mdi-chevron-down</v-icon>
          </button>

          <button type="button" class="product-mobile-sections__row" @click="openSection('allocation')">
            <v-icon class="product-mobile-sections__icon" size="22" color="#5B7C99">mdi-percent-outline</v-icon>
            <div class="product-mobile-sections__copy">
              <div class="product-mobile-sections__title">{{ $t("allocation") }}</div>
              <div class="product-mobile-sections__hint">{{ $t("allocationProductDescribe") }}</div>
            </div>
            <v-icon size="20" color="#B0BEC5">mdi-chevron-down</v-icon>
          </button>

          <button
            v-if="p.isVariableProduct"
            type="button"
            class="product-mobile-sections__row"
            @click="openSection('variants')"
          >
            <v-icon class="product-mobile-sections__icon" size="22" color="#5B7C99">mdi-shape-outline</v-icon>
            <div class="product-mobile-sections__copy">
              <div class="product-mobile-sections__title">{{ $t("variant-product") }}</div>
            </div>
            <v-icon size="20" color="#B0BEC5">mdi-chevron-down</v-icon>
          </button>

        </section>
        </div>

        <!-- Page tabs: heavy advanced content (not bottom sheets) -->
        <div v-if="mobileTab === 'pricing'" class="product-mobile-tab-panel">
          <pricing
            v-model="p.pricing"
            :costs="p.costs"
            :method="p.priceMethod"
            :value="p.pricing"
            :mobile="true"
          />
        </div>

        <div v-if="mobileTab === 'integrator'" class="product-mobile-tab-panel product-mobile-integ">
          <div class="product-mobile-integ__chips">
            <button
              v-for="tab in integratorSubTabs"
              :key="'integ-' + tab.key"
              type="button"
              class="product-mobile-integ__chip"
              :class="{ 'product-mobile-integ__chip--on': integratorSubTab === tab.key }"
              @click="setIntegratorSubTab(tab.key)"
            >
              <v-icon
                v-if="tab.icon"
                size="16"
                :color="integratorSubTab === tab.key ? '#fff' : '#5B7C99'"
              >
                {{ tab.icon }}
              </v-icon>
              <img
                v-else-if="tab.img"
                :src="tab.img"
                :alt="tab.label"
                class="product-mobile-integ__chip-logo"
                :class="{ 'product-mobile-integ__chip-logo--sm': tab.imgSm }"
              >
              <span>{{ tab.label }}</span>
            </button>
          </div>

          <!-- WooCommerce -->
          <div v-show="integratorSubTab === 'woocommerce'" class="product-mobile-integ__panel">
            <button
              type="button"
              class="product-mobile-integ__add"
              :disabled="p.isLoading"
              @click="addWooMobile"
            >
              <v-icon size="18" color="#5A6B85">mdi-plus</v-icon>
              <span>{{ $t("addId") }}</span>
            </button>
            <div
              v-if="!(p.woocommerceIdItems && p.woocommerceIdItems.length)"
              class="product-mobile-integ__empty"
            >
              <v-icon size="28" color="#90A4AE">mdi-link-variant-off</v-icon>
              <div class="product-mobile-integ__empty-title">{{ $t('no-data') }}</div>
              <div class="product-mobile-integ__empty-hint">{{ $t('addId') }}</div>
            </div>
            <div
              v-for="(item, h) in p.woocommerceIdItems"
              :key="'woo-m-' + h"
              class="product-mobile-integ__card"
              :class="{ 'product-mobile-integ__card--open': wooOpenIndex === h }"
            >
              <div
                class="product-mobile-integ__card-top"
                role="button"
                tabindex="0"
                @click="toggleWooCard(h)"
                @keydown.enter.prevent="toggleWooCard(h)"
                @keydown.space.prevent="toggleWooCard(h)"
              >
                <div class="product-mobile-integ__card-head">
                  <v-icon size="20" color="#78909C">
                    {{ wooOpenIndex === h ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                  <div class="product-mobile-integ__card-titles">
                    <span class="product-mobile-integ__card-title">{{ $t('row') }} {{ h + 1 }}</span>
                    <span class="product-mobile-integ__card-summary">{{ wooCardSummary(item) }}</span>
                  </div>
                </div>
                <button
                  type="button"
                  class="product-mobile-composer-card__remove"
                  :aria-label="$t('remove')"
                  @click.stop="p.removeWoocommerceId(item)"
                >
                  <v-icon size="20" color="#E91E63">mdi-trash-can-outline</v-icon>
                </button>
              </div>
              <div v-show="wooOpenIndex === h" class="product-mobile-integ__card-body">
                <div class="product-mobile-composer-field">
                  <div class="product-mobile-composer-field__label">{{ $t('woocommercePanelName') }}</div>
                  <autocomplete
                    v-model="item.woocommercePanelName"
                    title=""
                    :value="item.woocommercePanelName"
                    type="woocommercePanelName"
                    class="product-mobile-integ__picker"
                  />
                </div>
                <div class="product-mobile-composer-field">
                  <div class="product-mobile-composer-field__label">{{ $t('woocommerceId') }}</div>
                  <input
                    v-model="item.woocommerceId"
                    type="text"
                    class="product-mobile-integ__input"
                    :placeholder="$t('woocommerceId')"
                    autocomplete="off"
                  >
                </div>
                <div class="product-mobile-composer-field">
                  <div class="product-mobile-composer-field__label">{{ $t('woocommerceVariantId') }}</div>
                  <input
                    v-model="item.woocommerceVariantId"
                    type="text"
                    class="product-mobile-integ__input"
                    :placeholder="$t('woocommerceVariantId')"
                    autocomplete="off"
                  >
                </div>
                <div class="product-mobile-composer-field">
                  <div class="product-mobile-composer-field__label">{{ $t('woocommerceFixCount') }}</div>
                  <input
                    v-model="item.woocommerceFixCount"
                    type="text"
                    class="product-mobile-integ__input"
                    :placeholder="$t('woocommerceFixCount')"
                    autocomplete="off"
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- Digikala -->
          <div v-show="integratorSubTab === 'digikala'" class="product-mobile-integ__panel product-mobile-integ__embed">
            <digikala-integrator
              :digikala-panel="p.digikalaPanel"
              :mobile="true"
              @update="p.getPanels"
            />
          </div>

          <!-- Basalam -->
          <div v-show="integratorSubTab === 'basalam'" class="product-mobile-integ__panel">
            <button
              type="button"
              class="product-mobile-integ__add"
              :disabled="p.isLoading"
              @click="addBasalamMobile"
            >
              <v-icon size="18" color="#5A6B85">mdi-plus</v-icon>
              <span>{{ $t("addId") }}</span>
            </button>
            <div
              v-if="!(p.basalamIdItems && p.basalamIdItems.length)"
              class="product-mobile-integ__empty"
            >
              <v-icon size="28" color="#90A4AE">mdi-link-variant-off</v-icon>
              <div class="product-mobile-integ__empty-title">{{ $t('no-data') }}</div>
              <div class="product-mobile-integ__empty-hint">{{ $t('addId') }}</div>
            </div>
            <div
              v-for="(item, h) in p.basalamIdItems"
              :key="'bas-m-' + h"
              class="product-mobile-integ__card"
              :class="{ 'product-mobile-integ__card--open': basalamOpenIndex === h }"
            >
              <div
                class="product-mobile-integ__card-top"
                role="button"
                tabindex="0"
                @click="toggleBasalamCard(h)"
                @keydown.enter.prevent="toggleBasalamCard(h)"
                @keydown.space.prevent="toggleBasalamCard(h)"
              >
                <div class="product-mobile-integ__card-head">
                  <v-icon size="20" color="#78909C">
                    {{ basalamOpenIndex === h ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                  <div class="product-mobile-integ__card-titles">
                    <span class="product-mobile-integ__card-title">{{ $t('row') }} {{ h + 1 }}</span>
                    <span class="product-mobile-integ__card-summary">{{ basalamCardSummary(item) }}</span>
                  </div>
                </div>
                <button
                  type="button"
                  class="product-mobile-composer-card__remove"
                  :aria-label="$t('remove')"
                  @click.stop="p.removeBaSalamId(item)"
                >
                  <v-icon size="20" color="#E91E63">mdi-trash-can-outline</v-icon>
                </button>
              </div>
              <div v-show="basalamOpenIndex === h" class="product-mobile-integ__card-body">
                <div class="product-mobile-composer-field">
                  <div class="product-mobile-composer-field__label">{{ $t('basalamPanelName') }}</div>
                  <autocomplete
                    v-model="item.basalamPanelName"
                    title=""
                    :value="item.basalamPanelName"
                    type="basalamPanelName"
                    class="product-mobile-integ__picker"
                  />
                </div>
                <div class="product-mobile-composer-field">
                  <div class="product-mobile-composer-field__label">{{ $t('basalamId') }}</div>
                  <input
                    v-model="item.basalamId"
                    type="text"
                    class="product-mobile-integ__input"
                    :placeholder="$t('basalamId')"
                    autocomplete="off"
                  >
                </div>
                <div class="product-mobile-composer-field">
                  <div class="product-mobile-composer-field__label">{{ $t('basalamVariantId') }}</div>
                  <input
                    v-model="item.basalamVariantId"
                    type="text"
                    class="product-mobile-integ__input"
                    :placeholder="$t('basalamVariantId')"
                    autocomplete="off"
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- Markito -->
          <div v-show="integratorSubTab === 'markito'" class="product-mobile-integ__panel">
            <div class="product-mobile-integ__simple">
              <div class="product-mobile-composer-field">
                <div class="product-mobile-composer-field__label">{{ $t('markitoId') }}</div>
                <input
                  v-model="p.markitoId"
                  type="text"
                  class="product-mobile-integ__input"
                  :placeholder="$t('markitoId')"
                  autocomplete="off"
                >
              </div>
            </div>
          </div>

          <!-- Portal -->
          <div v-show="integratorSubTab === 'portal'" class="product-mobile-integ__panel">
            <div class="product-mobile-integ__simple">
              <div class="product-mobile-composer-field">
                <div class="product-mobile-composer-field__label">{{ $t('portalId') }}</div>
                <input
                  v-model="p.portalId"
                  type="text"
                  class="product-mobile-integ__input"
                  :placeholder="$t('portalId')"
                  autocomplete="off"
                >
              </div>
              <div class="product-mobile-composer-field">
                <div class="product-mobile-composer-field__label">{{ $t('portalVariantId') }}</div>
                <input
                  v-model="p.portalVariantId"
                  type="text"
                  class="product-mobile-integ__input"
                  :placeholder="$t('portalVariantId')"
                  autocomplete="off"
                >
              </div>
            </div>
          </div>

          <!-- Portal3 -->
          <div v-show="integratorSubTab === 'portal3'" class="product-mobile-integ__panel product-mobile-integ__embed">
            <Portal3Integrator
              :portal-panel="p.portalPanel"
              :mobile="true"
              @updatePortal="p.getPortalPanels"
            />
          </div>

          <!-- Sazito -->
          <div v-show="integratorSubTab === 'sazito'" class="product-mobile-integ__panel product-mobile-integ__embed">
            <sazito-integrator
              :sazito-panel="p.sazitoPanel"
              :mobile="true"
              @updateSazito="p.getSazitoPanels"
            />
          </div>

          <!-- Webzi -->
          <div v-show="integratorSubTab === 'webzi'" class="product-mobile-integ__panel">
            <div class="product-mobile-integ__simple">
              <div class="product-mobile-composer-field">
                <div class="product-mobile-composer-field__label">{{ $t('webziId') }}</div>
                <input
                  v-model="p.webziId"
                  type="text"
                  class="product-mobile-integ__input"
                  :placeholder="$t('webziId')"
                  autocomplete="off"
                >
              </div>
              <div class="product-mobile-composer-field">
                <div class="product-mobile-composer-field__label">{{ $t('webziVariantId') }}</div>
                <input
                  v-model="p.webziVariantId"
                  type="text"
                  class="product-mobile-integ__input"
                  :placeholder="$t('webziVariantId')"
                  autocomplete="off"
                >
              </div>
            </div>
          </div>

          <!-- Mixin -->
          <div v-show="integratorSubTab === 'mixin'" class="product-mobile-integ__panel product-mobile-integ__embed">
            <mixin-integrator :mobile="true" :mixin-panel="p.mixinPanel" @updateMixin="p.getMixinPanels" />
          </div>

          <!-- Snapshop -->
          <div v-show="integratorSubTab === 'snapshop'" class="product-mobile-integ__panel product-mobile-integ__embed">
            <snapshop-integrator :mobile="true" :snapshop-panel="p.snapshopPanel" @updateSnapshop="p.getSnapshopPanels" />
          </div>
        </div>

        <div v-if="mobileTab === 'shop'" class="product-mobile-tab-panel">
          <tiptap-vuetify
            v-model="p.body"
            :extensions="p.extensions"
            :toolbar-attributes="{ color: '#ababab', dark: true }"
          />
        </div>
      </div>

      <div class="product-mobile-actions" :style="actionsStyle">
        <div class="product-mobile-actions__row">
          <v-btn
            v-if="!p.id"
            class="product-mobile-actions__primary"
            height="36"
            :loading="p.isLoading"
            @click="submit(true)"
          >
            {{ $t("submit") }}
          </v-btn>
          <v-btn
            v-else
            class="product-mobile-actions__primary"
            height="36"
            :loading="p.isLoading"
            @click="submitUpdate"
          >
            {{ $t("submit") }}
          </v-btn>
          <v-btn
            v-if="!p.id"
            class="product-mobile-actions__secondary"
            outlined
            height="36"
            :loading="p.isLoading"
            @click="submit(false)"
          >
            {{ $t("submit&new") }}
          </v-btn>
        </div>
      </div>
    </v-form>

    <!-- Text entry sheet -->
    <v-bottom-sheet v-model="textSheet" eager>
      <v-card class="amf-sheet" dir="rtl">
        <div class="amf-sheet__handle" />
        <div class="amf-sheet__header">
          <span class="amf-sheet__header-spacer" />
          <div class="amf-sheet__title">{{ textSheetLabel }}</div>
          <button type="button" class="amf-sheet__back" @click="textSheet = false">
            <v-icon size="22">mdi-close</v-icon>
          </button>
        </div>
        <div class="amf-sheet__body amf-sheet__body--stack">
          <v-text-field
            v-model="textSheetDraft"
            :label="textSheetLabel"
            dense
            outlined
            autofocus
            clearable
            hide-details="auto"
            @keyup.enter="confirmTextSheet"
          />
          <v-btn color="primary" block height="44" @click="confirmTextSheet">
            {{ $t("confirm") || $t("submit") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Hidden hosts: open native mobile fullscreen pickers (same as tags) -->
    <div class="product-mobile-picker-host" aria-hidden="true">
      <aicomplete
        ref="refUnit"
        v-model="p.unit"
        :val="p.unit"
        title="unit"
        type="unit"
        :set-init="true"
      />
      <autocomplete
        ref="refKind"
        v-model="p.kind"
        :title="$t('productKind')"
        type="productKind"
        :value="p.kind"
      />
      <aicomplete
        ref="refGroup"
        v-model="p.group"
        :val="p.group"
        title="main-group"
        type="productGroup"
        :data="JSON.stringify({ parent: 0, type: 'group' })"
        :new-btn="true"
        :set-init="!p.id"
      />
      <aicomplete
        ref="refCat1"
        v-model="p.cat1"
        :val="p.cat1"
        title="other-group"
        type="productGroup"
        :data="JSON.stringify({ parent: p.group ? p.group.id : null, type: 'category' })"
        :new-btn="true"
        :set-init="!p.id"
      />
      <aicomplete
        ref="refCat2"
        v-model="p.cat2"
        :val="p.cat2"
        title="category"
        type="productGroup"
        :data="JSON.stringify({ parent: p.cat1 ? p.cat1.id : null, type: 'subcategory' })"
        :new-btn="true"
        :set-init="!p.id"
      />
      <tag ref="refTag" v-model="p.tag" :solo="false" :input="p.tag" />
      <autocomplete
        ref="refBranch"
        v-model="p.branch"
        :title="$t('branch')"
        type="branch"
        :value="p.branch"
      />
    </div>

    <!-- Numpad -->
    <v-bottom-sheet v-model="numpadOpen" @click:outside="numpadOpen = false">
      <v-card class="amf-numpad" dir="rtl">
        <div class="amf-numpad__header">
          <span class="amf-numpad__header-spacer" />
          <div class="amf-numpad__title">{{ numpadLabel }}</div>
          <button type="button" class="amf-sheet__back" @click="numpadOpen = false">
            <v-icon size="22">mdi-close</v-icon>
          </button>
        </div>
        <div class="amf-numpad__display">
          <div class="amf-numpad__display-label">{{ numpadLabel }}</div>
          <div class="amf-numpad__display-value">{{ numpadDisplay }}</div>
        </div>
        <div class="amf-numpad__pad" dir="ltr">
          <div class="amf-numpad__digits">
            <button
              v-for="n in [1, 2, 3, 4, 5, 6, 7, 8, 9]"
              :key="n"
              type="button"
              class="amf-numpad__key"
              @click="numpadDigit(n)"
            >
              {{ n }}
            </button>
            <button type="button" class="amf-numpad__key amf-numpad__key--multi" @click="numpadAppend('00')">
              00
            </button>
            <button type="button" class="amf-numpad__key" @click="numpadDigit(0)">0</button>
            <button type="button" class="amf-numpad__key amf-numpad__key--multi" @click="numpadAppend('000')">
              000
            </button>
          </div>
          <div class="amf-numpad__actions">
            <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadStep(1)">+</button>
            <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadStep(-1)">-</button>
            <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadClear">C</button>
            <button
              type="button"
              class="amf-numpad__key amf-numpad__key--action"
              :disabled="!numpadIsAmount"
              @click="numpadDot"
            >
              .
            </button>
            <button type="button" class="amf-numpad__key amf-numpad__key--action" @click="numpadBackspace">
              <v-icon size="20">mdi-backspace-outline</v-icon>
            </button>
          </div>
        </div>
        <div class="amf-numpad__footer">
          <v-btn color="primary" block height="48" @click="confirmNumpad">
            {{ $t("confirm") || $t("submit") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Generic section sheet: sticky add + scroll body + sticky confirm -->
    <v-bottom-sheet v-model="sectionSheet" eager>
      <v-card class="amf-sheet product-mobile-section-sheet" dir="rtl">
        <div class="amf-sheet__handle" />
        <div class="amf-sheet__header">
          <span class="amf-sheet__header-spacer" />
          <div class="amf-sheet__title">{{ sectionTitle }}</div>
          <button
            type="button"
            class="amf-sheet__back"
            :aria-label="$t('back')"
            @click="sectionSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div
          v-if="sectionHasAddAction"
          class="product-mobile-section-sheet__toolbar product-mobile-section-sheet__toolbar--composer"
        >
          <div class="product-mobile-section-sheet__hint">
            {{ sectionAddHint }}
          </div>
          <button
            type="button"
            class="product-mobile-section-sheet__add"
            @click="onSectionAdd"
          >
            <v-icon size="18" color="#5A6B85">mdi-plus</v-icon>
            <span>{{ sectionAddLabel }}</span>
          </button>
        </div>

        <div class="product-mobile-section-sheet__scroll">
          <!-- Tax & pricing -->
          <template v-if="activeSection === 'tax'">
            <div class="product-mobile-sheet-fields">
              <div
                class="product-mobile-field product-mobile-field--select"
                @click="openNumpad('taxid', $t('taxisProduct'), false)"
              >
                <span class="product-mobile-field__label">{{ $t("taxisProduct") }}</span>
                <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                  <span :class="{ 'product-mobile-field__placeholder': !hasValue(p.taxid) }">
                    {{ hasValue(p.taxid) ? p.taxid : "—" }}
                  </span>
                </button>
              </div>

              <div
                class="product-mobile-field product-mobile-field--select"
                @click="openNumpad('taxPercent', $t('taxPercent'), false)"
              >
                <span class="product-mobile-field__label">{{ $t("taxPercent") }}</span>
                <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                  <span :class="{ 'product-mobile-field__placeholder': !hasValue(p.taxPercent) }">
                    {{ hasValue(p.taxPercent) ? p.taxPercent : "—" }}
                  </span>
                </button>
              </div>

              <template v-if="!p.isVariableProduct">
                <div
                  class="product-mobile-field product-mobile-field--select"
                  @click="openNumpad('price', $t('fixSellPrice'), true)"
                >
                  <span class="product-mobile-field__label">{{ $t("fixSellPrice") }}</span>
                  <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                    <span :class="{ 'product-mobile-field__placeholder': !hasValue(p.price) }">
                      {{ formatMoney(p.price) }}
                    </span>
                  </button>
                </div>

                <div
                  class="product-mobile-field product-mobile-field--select"
                  @click="openNumpad('priceWithTax', $t('priceWithTax'), true)"
                >
                  <span class="product-mobile-field__label">{{ $t("priceWithTax") }}</span>
                  <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                    <span :class="{ 'product-mobile-field__placeholder': !hasValue(p.priceWithTax) }">
                      {{ formatMoney(p.priceWithTax) }}
                    </span>
                  </button>
                </div>

                <div
                  class="product-mobile-field product-mobile-field--select"
                  @click="openNumpad('discount', $t('discount'), true)"
                >
                  <span class="product-mobile-field__label">{{ $t("discount") }}</span>
                  <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                    <span :class="{ 'product-mobile-field__placeholder': !hasValue(p.discount) }">
                      {{ formatMoney(p.discount) }}
                    </span>
                  </button>
                </div>

                <div
                  class="product-mobile-field product-mobile-field--select"
                  @click="openNumpad('discountPercent', $t('discountPercent'), false)"
                >
                  <span class="product-mobile-field__label">{{ $t("discountPercent") }}</span>
                  <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                    <span :class="{ 'product-mobile-field__placeholder': !hasValue(p.discountPercent) }">
                      {{ hasValue(p.discountPercent) ? p.discountPercent : "—" }}
                    </span>
                  </button>
                </div>

                <div class="product-mobile-field product-mobile-field--switch">
                  <span class="product-mobile-field__label">{{ $t("isKalaBarg") }}</span>
                  <v-switch
                    class="mt-0 pt-0 product-mobile-field__switch"
                    dense
                    inset
                    hide-details
                    :input-value="!!p.isKalaBarg"
                    color="#5B7C99"
                    @change="(v) => (p.isKalaBarg = !!v)"
                  />
                </div>
              </template>

              <template v-if="p.type === 'product' && p.kind && p.kind.id === 112">
                <div
                  class="product-mobile-field product-mobile-field--select"
                  @click="openNumpad('constructionWagesPercent', $t('construction_wages_percent'), false)"
                >
                  <span class="product-mobile-field__label">{{ $t("construction_wages_percent") }}</span>
                  <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                    <span :class="{ 'product-mobile-field__placeholder': !hasValue(p.constructionWagesPercent) }">
                      {{ hasValue(p.constructionWagesPercent) ? p.constructionWagesPercent : "—" }}
                    </span>
                  </button>
                </div>
                <div
                  class="product-mobile-field product-mobile-field--select"
                  @click="openNumpad('profitPercent', $t('profit_percent'), false)"
                >
                  <span class="product-mobile-field__label">{{ $t("profit_percent") }}</span>
                  <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                    <span :class="{ 'product-mobile-field__placeholder': !hasValue(p.profitPercent) }">
                      {{ hasValue(p.profitPercent) ? p.profitPercent : "—" }}
                    </span>
                  </button>
                </div>
                <div
                  class="product-mobile-field product-mobile-field--select"
                  @click="openNumpad('commissionPercent', $t('commission_percent'), false)"
                >
                  <span class="product-mobile-field__label">{{ $t("commission_percent") }}</span>
                  <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                    <span :class="{ 'product-mobile-field__placeholder': !hasValue(p.commissionPercent) }">
                      {{ hasValue(p.commissionPercent) ? p.commissionPercent : "—" }}
                    </span>
                  </button>
                </div>
              </template>
            </div>
          </template>

          <!-- Other barcodes -->
          <template v-else-if="activeSection === 'barcodes'">
            <div
              v-if="!p.barcodes || !p.barcodes.length"
              class="product-mobile-section-sheet__empty"
            >
              هنوز بارکدی اضافه نشده است.
            </div>
            <div
              v-for="(item, i) in p.barcodes"
              :key="'bc-' + i"
              class="product-mobile-inline-row mb-2"
            >
              <v-text-field
                v-model="item.value"
                :label="$t('barcode')"
                dense
                outlined
                :rules="[(v) => !!v || $t('is-required')]"
                clearable
                hide-details="auto"
                class="flex-grow-1"
              />
              <v-btn icon color="pink" @click="p.removeBarcode(item)">
                <v-icon>mdi-trash-can-outline</v-icon>
              </v-btn>
            </div>
          </template>

          <!-- Grouping -->
          <template v-else-if="activeSection === 'grouping'">
            <div class="product-mobile-sheet-fields">
              <div
                class="product-mobile-field product-mobile-field--select"
                @click="openPicker('refGroup')"
              >
                <span class="product-mobile-field__label">{{ $t("main-group") }}</span>
                <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                  <span :class="{ 'product-mobile-field__placeholder': !groupText }">
                    {{ groupText || "—" }}
                  </span>
                </button>
              </div>
              <div
                class="product-mobile-field product-mobile-field--select"
                @click="openPicker('refCat1')"
              >
                <span class="product-mobile-field__label">{{ $t("other-group") }}</span>
                <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                  <span :class="{ 'product-mobile-field__placeholder': !cat1Text }">
                    {{ cat1Text || "—" }}
                  </span>
                </button>
              </div>
              <div
                class="product-mobile-field product-mobile-field--select"
                @click="openPicker('refCat2')"
              >
                <span class="product-mobile-field__label">{{ $t("category") }}</span>
                <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                  <span :class="{ 'product-mobile-field__placeholder': !cat2Text }">
                    {{ cat2Text || "—" }}
                  </span>
                </button>
              </div>
              <div
                class="product-mobile-field product-mobile-field--select"
                @click="openPicker('refTag')"
              >
                <span class="product-mobile-field__label">{{ $t("tag") }}</span>
                <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                  <span :class="{ 'product-mobile-field__placeholder': !tagText }">
                    {{ tagText || "—" }}
                  </span>
                </button>
              </div>
            </div>
          </template>

          <!-- Dimensions -->
          <template v-else-if="activeSection === 'dimensions'">
            <div class="product-mobile-sheet-fields">
              <div
                class="product-mobile-field product-mobile-field--select"
                @click="openNumpad('length', $t('length'), true)"
              >
                <span class="product-mobile-field__label">{{ $t("length") }}</span>
                <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                  <span :class="{ 'product-mobile-field__placeholder': !hasValue(p.length) }">
                    {{ hasValue(p.length) ? p.length : "—" }}
                  </span>
                </button>
              </div>
              <div
                class="product-mobile-field product-mobile-field--select"
                @click="openNumpad('width', $t('width'), true)"
              >
                <span class="product-mobile-field__label">{{ $t("width") }}</span>
                <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                  <span :class="{ 'product-mobile-field__placeholder': !hasValue(p.width) }">
                    {{ hasValue(p.width) ? p.width : "—" }}
                  </span>
                </button>
              </div>
              <div
                class="product-mobile-field product-mobile-field--select"
                @click="openNumpad('height', $t('height'), true)"
              >
                <span class="product-mobile-field__label">{{ $t("height") }}</span>
                <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                  <span :class="{ 'product-mobile-field__placeholder': !hasValue(p.height) }">
                    {{ hasValue(p.height) ? p.height : "—" }}
                  </span>
                </button>
              </div>
              <div
                class="product-mobile-field product-mobile-field--select"
                @click="openNumpad('weight', $t('weight') + ' (kg)', true)"
              >
                <span class="product-mobile-field__label">{{ $t("weight") }} (kg)</span>
                <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                  <span :class="{ 'product-mobile-field__placeholder': !hasValue(p.weight) }">
                    {{ hasValue(p.weight) ? p.weight : "—" }}
                  </span>
                </button>
              </div>
            </div>
          </template>

          <!-- Initial stock -->
          <template v-else-if="activeSection === 'stock'">
            <div
              v-if="!p.initial_stocks || !p.initial_stocks.length"
              class="product-mobile-section-sheet__empty"
            >
              هنوز موجودی‌ای اضافه نشده است.
            </div>
            <div
              v-for="(item, i) in p.initial_stocks"
              :key="'st-' + i"
              class="product-mobile-stock mb-3"
            >
              <autocomplete
                v-model="item.entrepot"
                :title="$t('entrepot')"
                type="entrepot"
                :value="item.entrepot"
                class="mb-2"
              />
              <v-text-field
                v-model="item.stock"
                :label="$t('stock')"
                dense
                outlined
                type="number"
                clearable
                hide-details="auto"
                class="mb-2"
              />
              <v-btn
                v-if="p.has_serial === 'active'"
                small
                outlined
                color="teal"
                class="mb-2"
                @click="p.openInitialStockSerialDialog(item)"
              >
                {{ $t("product-serial") }}
              </v-btn>
              <v-btn icon color="pink" @click="p.removeInitialStock(i)">
                <v-icon>mdi-trash-can-outline</v-icon>
              </v-btn>
            </div>
          </template>

          <!-- Description -->
          <template v-else-if="activeSection === 'description'">
            <description ref="refContent" v-model="p.content" type="product" />
            <v-text-field
              v-if="!p.isVariableProduct"
              v-model="p.custom_code"
              :label="$t('Custom financial ID')"
              dense
              outlined
              clearable
              hide-details="auto"
              class="mt-3"
            />
          </template>

          <!-- Variants -->
          <template v-else-if="activeSection === 'variants'">
            <variable-product-tab
              v-model="p.variableVariants"
              :product-name="p.name"
              :costs="p.costs"
              :price-method="p.priceMethod"
              :initial-check="p.initialCheck"
              :product-type="p.type"
              :has-serial="p.has_serial"
            />
          </template>

          <!-- Extra images -->
          <template v-else-if="activeSection === 'images'">
            <file-picker
              ref="refProductImages"
              v-model="p.productImages"
              :label="$t('uploadImage')"
              :multiple="true"
              extensions="image/png, image/gif, image/jpeg"
              type-prop="productImages"
              :val="p.productImagesVal ? p.productImagesVal : null"
            />
          </template>

          <!-- Other units -->
          <template v-else-if="activeSection === 'units'">
            <div
              v-if="!p.units || !p.units.length"
              class="product-mobile-section-sheet__empty"
            >
              هنوز واحد فرعی اضافه نشده است.
            </div>
            <div
              v-for="(item, i) in p.units"
              :key="'unit-' + i"
              class="product-mobile-composer-card"
            >
              <div class="product-mobile-composer-card__head">
                <div class="product-mobile-composer-field product-mobile-composer-card__main">
                  <div class="product-mobile-composer-field__label-row">
                    <span class="product-mobile-composer-field__label">{{ $t("unit") }}</span>
                    <span class="product-mobile-composer-card__ratio">
                      1 {{ item.unit ? item.unit.name : "" }} =
                      {{ item.value ? item.value : 1 }}
                      {{ p.unit && p.unit.name ? p.unit.name : "" }}
                    </span>
                  </div>
                  <button
                    type="button"
                    class="product-mobile-composer-field__control"
                    @click="openUnitRowPicker(i)"
                  >
                    <span :class="{ 'product-mobile-field__placeholder': !entityLabel(item.unit) }">
                      {{ entityLabel(item.unit) || "—" }}
                    </span>
                  </button>
                  <autocomplete
                    :ref="'refSubUnit' + i"
                    v-model="item.unit"
                    :title="$t('unit')"
                    type="unit"
                    :value="item.unit"
                    :set-init="true"
                    :rules="[(v) => !!v || $t('is-required')]"
                    class="product-mobile-composer-card__picker"
                  />
                </div>
                <button
                  type="button"
                  class="product-mobile-composer-card__remove"
                  :aria-label="$t('remove')"
                  @click="p.removeUnit(item)"
                >
                  <v-icon size="20" color="#E91E63">mdi-close</v-icon>
                </button>
              </div>

              <div class="product-mobile-composer-field">
                <div class="product-mobile-composer-field__label">{{ $t("value") }}</div>
                <button
                  type="button"
                  class="product-mobile-composer-field__control product-mobile-composer-field__control--numpad"
                  @click="openNumpad('value', $t('value'), true, item)"
                >
                  {{ hasValue(item.value) ? item.value : "—" }}
                </button>
              </div>

              <div class="product-mobile-composer-card__switch">
                <span class="product-mobile-composer-field__label">{{ $t("defaultUnit") }}</span>
                <v-switch
                  class="mt-0 pt-0 product-mobile-field__switch"
                  dense
                  inset
                  hide-details
                  :input-value="item.default === 'active' || item.default === true"
                  color="#5B7C99"
                  @change="onUnitDefaultChange(item, i, $event)"
                />
              </div>
            </div>
          </template>

          <!-- Custom fields -->
          <template v-else-if="activeSection === 'customFields'">
            <custom-fields
              ref="refCustomFields"
              v-model="p.customFields"
              :value="p.customFields"
              type-prop="productCustomFields"
              :mobile="true"
            />
          </template>

          <!-- Other settings -->
          <template v-else-if="activeSection === 'otherSetting'">
            <div class="product-mobile-sheet-fields">
              <div
                v-if="p.type === 'product'"
                class="product-mobile-composer-field product-mobile-composer-field--tight"
              >
                <div class="product-mobile-composer-field__label">{{ $t("inventoryMethod") }}</div>
                <v-select
                  v-model="p.inventoryMethod"
                  :items="p.inventoryMethodItems"
                  item-value="value"
                  item-text="text"
                  solo
                  flat
                  dense
                  hide-details
                  height="40"
                  append-icon="mdi-chevron-down"
                  class="product-mobile-select-control"
                />
              </div>

              <div
                v-if="p.type === 'product'"
                class="product-mobile-composer-field product-mobile-composer-field--tight"
              >
                <div class="product-mobile-composer-field__label">{{ $t("priceMethod") }}</div>
                <v-select
                  v-model="p.priceMethod"
                  :items="p.priceMethodItems"
                  item-value="value"
                  item-text="text"
                  solo
                  flat
                  dense
                  hide-details
                  height="40"
                  append-icon="mdi-chevron-down"
                  class="product-mobile-select-control"
                />
              </div>

              <div
                class="product-mobile-field product-mobile-field--select product-mobile-field--divider-top"
                @click="openPicker('refBranch')"
              >
                <span class="product-mobile-field__label">{{ $t("branch") }}</span>
                <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                  <span :class="{ 'product-mobile-field__placeholder': !branchText }">
                    {{ branchText || "—" }}
                  </span>
                </button>
              </div>

              <div
                v-if="p.type === 'product' && !p.isVariableProduct"
                class="product-mobile-field product-mobile-field--select"
                @click="openNumpad('orderPoint', $t('orderPoint'), false)"
              >
                <span class="product-mobile-field__label">{{ $t("orderPoint") }}</span>
                <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                  <span :class="{ 'product-mobile-field__placeholder': !hasValue(p.orderPoint) }">
                    {{ hasValue(p.orderPoint) ? p.orderPoint : "—" }}
                  </span>
                </button>
              </div>

              <div
                v-if="p.type === 'product' && !p.isVariableProduct"
                class="product-mobile-field product-mobile-field--select"
                @click="investorSheet = true"
              >
                <span class="product-mobile-field__label">{{ $t("investor") }}</span>
                <button type="button" class="product-mobile-field__control product-mobile-field__control--select">
                  <span :class="{ 'product-mobile-field__placeholder': !investorRows.length }">
                    {{ investorRows.length ? investorRows.length : "—" }}
                  </span>
                  <v-icon size="18" color="#B0BEC5">mdi-chevron-left</v-icon>
                </button>
              </div>
            </div>
          </template>

          <!-- Allocation -->
          <template v-else-if="activeSection === 'allocation'">
            <div
              v-if="!p.allocations || !p.allocations.length"
              class="product-mobile-section-sheet__empty"
            >
              هنوز تخصیصی اضافه نشده است.
            </div>
            <div
              v-for="(item, i) in p.allocations"
              :key="'alloc-' + i"
              class="product-mobile-composer-card"
            >
              <div class="product-mobile-composer-card__head">
                <div class="product-mobile-composer-field product-mobile-composer-card__main">
                  <div class="product-mobile-composer-field__label">
                    {{ $t("allocationPercent") }}
                  </div>
                  <button
                    type="button"
                    class="product-mobile-composer-field__control product-mobile-composer-field__control--numpad"
                    @click="openNumpad('percent', $t('allocationPercent'), true, item)"
                  >
                    <template v-if="hasValue(item.percent)">
                      {{ item.percent }}%
                    </template>
                    <span v-else class="product-mobile-field__placeholder">—</span>
                  </button>
                </div>
                <button
                  type="button"
                  class="product-mobile-composer-card__remove"
                  :aria-label="$t('remove')"
                  @click="p.removeAllocation(item)"
                >
                  <v-icon size="20" color="#E91E63">mdi-close</v-icon>
                </button>
              </div>

              <div class="product-mobile-composer-field">
                <div class="product-mobile-composer-field__label">{{ $t("fromDate") }}</div>
                <button
                  type="button"
                  class="product-mobile-composer-field__control"
                  @click="openDateSheet(item, 'from_date', $t('fromDate'))"
                >
                  <span :class="{ 'product-mobile-field__placeholder': !hasValue(item.from_date) }">
                    {{ hasValue(item.from_date) ? item.from_date : "—" }}
                  </span>
                </button>
              </div>

              <div class="product-mobile-composer-field">
                <div class="product-mobile-composer-field__label">{{ $t("toDate") }}</div>
                <button
                  type="button"
                  class="product-mobile-composer-field__control"
                  @click="openDateSheet(item, 'to_date', $t('toDate'))"
                >
                  <span :class="{ 'product-mobile-field__placeholder': !hasValue(item.to_date) }">
                    {{ hasValue(item.to_date) ? item.to_date : "—" }}
                  </span>
                </button>
              </div>
            </div>
          </template>

        </div>

        <div class="product-mobile-section-sheet__footer">
          <button
            type="button"
            class="product-mobile-section-sheet__save"
            @click="sectionSheet = false"
          >
            {{ $t("confirm") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Investor nested sheet -->
    <v-bottom-sheet v-model="investorSheet" eager>
      <v-card class="amf-sheet product-mobile-section-sheet" dir="rtl">
        <div class="amf-sheet__handle" />
        <div class="amf-sheet__header">
          <span class="amf-sheet__header-spacer" />
          <div class="amf-sheet__title">{{ $t("investor") }}</div>
          <button
            type="button"
            class="amf-sheet__back"
            :aria-label="$t('back')"
            @click="investorSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="product-mobile-section-sheet__toolbar product-mobile-section-sheet__toolbar--composer">
          <div class="product-mobile-section-sheet__hint">
            برای افزودن سهامدار، کلیک کنید
          </div>
          <button
            type="button"
            class="product-mobile-section-sheet__add"
            @click="p.addInvestorRow"
          >
            <v-icon size="18" color="#5A6B85">mdi-plus</v-icon>
            <span>{{ $t("add") }} {{ $t("investor") }}</span>
          </button>
        </div>

        <div class="product-mobile-section-sheet__scroll">
          <div
            v-if="!investorRows.length"
            class="product-mobile-section-sheet__empty"
          >
            هنوز سهامداری اضافه نشده است.
          </div>

          <div
            v-for="(item, i) in investorRows"
            :key="'inv-' + i"
            class="product-mobile-composer-card"
          >
            <div class="product-mobile-composer-card__head">
              <div class="product-mobile-composer-field product-mobile-composer-card__main">
                <div class="product-mobile-composer-field__label">{{ $t("investor") }}</div>
                <button
                  type="button"
                  class="product-mobile-composer-field__control"
                  @click="openInvestorRowPicker(i)"
                >
                  <span :class="{ 'product-mobile-field__placeholder': !entityLabel(item.investor) }">
                    {{ entityLabel(item.investor) || "—" }}
                  </span>
                </button>
                <autocomplete
                  :ref="'refInvestor' + i"
                  v-model="item.investor"
                  :title="$t('investor')"
                  type="person"
                  :value="item.investor"
                  :new-btn="true"
                  :data="JSON.stringify({ type: 'investor' })"
                  :rules="[(v) => !!v || $t('is-required')]"
                  class="product-mobile-composer-card__picker"
                  @input="p.changeInvestor(item)"
                />
              </div>
              <button
                type="button"
                class="product-mobile-composer-card__remove"
                :aria-label="$t('remove')"
                @click="p.removeInvestor(item)"
              >
                <v-icon size="20" color="#E91E63">mdi-close</v-icon>
              </button>
            </div>

            <div class="product-mobile-composer-field">
              <div class="product-mobile-composer-field__label">{{ $t("investorSellPercent") }}</div>
              <button
                type="button"
                class="product-mobile-composer-field__control product-mobile-composer-field__control--numpad"
                @click="openNumpad('investorSellPercent', $t('investorSellPercent'), true, item)"
              >
                {{ hasValue(item.investorSellPercent) ? item.investorSellPercent : "—" }}
              </button>
            </div>

            <div class="product-mobile-composer-field">
              <div class="product-mobile-composer-field__label">{{ $t("investorReturnSellPercent") }}</div>
              <button
                type="button"
                class="product-mobile-composer-field__control product-mobile-composer-field__control--numpad"
                @click="openNumpad('investorReturnSellPercent', $t('investorReturnSellPercent'), true, item)"
              >
                {{ hasValue(item.investorReturnSellPercent) ? item.investorReturnSellPercent : "—" }}
              </button>
            </div>
          </div>
        </div>

        <div class="product-mobile-section-sheet__footer">
          <button
            type="button"
            class="product-mobile-section-sheet__save"
            @click="investorSheet = false"
          >
            {{ $t("confirm") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Mobile date layer (same pattern as invoice mobile) -->
    <v-bottom-sheet v-model="dateSheet" eager>
      <v-card class="amf-sheet product-mobile-section-sheet" dir="rtl">
        <div class="amf-sheet__handle" />
        <div class="amf-sheet__header">
          <span class="amf-sheet__header-spacer" />
          <div class="amf-sheet__title">{{ dateSheetLabel }}</div>
          <button
            type="button"
            class="amf-sheet__back"
            :aria-label="$t('back')"
            @click="dateSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="product-mobile-section-sheet__scroll">
          <div class="product-mobile-date">
            <div class="product-mobile-date__selected">
              <span class="product-mobile-date__label">{{ dateSheetLabel }}</span>
              <strong class="product-mobile-date__value">
                {{ dateSheetDisplay || "—" }}
              </strong>
              <button
                v-if="dateSheetDisplay"
                type="button"
                class="product-mobile-date__clear"
                @click="clearDateSheetValue"
              >
                {{ $t("clear") }}
              </button>
            </div>
            <date-picker
              v-if="dateSheet && dateSheetTarget"
              v-model="dateSheetProxy"
              inline
              simple
              auto-submit
              :locale="$store.state.date_locale"
              :format="$store.state.date_format"
              class="product-mobile-date__picker"
              color="#7B8BA3"
            />
          </div>
        </div>
        <div class="product-mobile-section-sheet__footer">
          <button
            type="button"
            class="product-mobile-section-sheet__save"
            @click="dateSheet = false"
          >
            {{ $t("confirm") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <serial-management-dialog
      v-model="p.serialDialog"
      :product="p.serialDialogProduct"
      :entrepot="(p.serialItem && p.serialItem.entrepot) || {}"
      :total-count="p.serialItem && p.serialItem.stock"
      :serials="(p.serialItem && p.serialItem.serials) || []"
      serial-flow="in"
      :selected-serials="(p.serialItem && p.serialItem.serials) || []"
      @confirm="p.setInitialStockSerials"
    />

    <v-snackbar
      v-model="p.snakbar"
      :color="p.snakbarColor ? p.snakbarColor : 'primary'"
      top
      style="z-index: 1000000000000 !important"
    >
      {{ p.snakbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import General from "../../../../../../Mixins/general";
import filePicker from "../../../../components/general/filePicker/index";
import Pricing from "../../pricing";
import DigikalaIntegrator from "../../partial/DigikalaIntegrator";
import SazitoIntegrator from "../../partial/SazitoIntegrator.vue";
import Portal3Integrator from "../../partial/Portal3Integrator.vue";
import MixinIntegrator from "../../partial/MixinIntegrator.vue";
import SnapshopIntegrator from "../../partial/SnapshopIntegrator.vue";
import Description from "../../../../components/general/description/Description";
import VariableProductTab from "../../partial/VariableProductTab.vue";
import SerialManagementDialog from "../../../../components/general/serials/SerialManagementDialog";
import customFields from "../../../../components/general/customFields/index.vue";
import { TiptapVuetify } from "tiptap-vuetify";

/**
 * Mobile product form UI. Logic lives on product.vue (inject productForm).
 */
export default {
  name: "ProductFormMobile",
  inject: ["productForm"],
  mixins: [General],
  components: {
    autocomplete: () =>
      import("../../../../components/general/patial/autocomplete"),
    aicomplete: () => import("../../../../components/general/AIcomplete/index"),
    TiptapVuetify,
    filePicker,
    Pricing,
    Tag: () => import("../../../../components/general/Tag/tag"),
    DigikalaIntegrator,
    SazitoIntegrator,
    Portal3Integrator,
    MixinIntegrator,
    SnapshopIntegrator,
    Description,
    VariableProductTab,
    SerialManagementDialog,
    customFields,
  },
  data() {
    return {
      mobileValid: true,
      textSheet: false,
      textSheetField: null,
      textSheetLabel: "",
      textSheetDraft: "",
      sectionSheet: false,
      activeSection: null,
      numpadOpen: false,
      numpadField: null,
      numpadTarget: null,
      numpadLabel: "",
      numpadDraft: "",
      numpadIsAmount: true,
      summaryMoreOpen: false,
      mobileTab: "main",
      integratorSubTab: "woocommerce",
      wooOpenIndex: 0,
      basalamOpenIndex: 0,
      investorSheet: false,
      dateSheet: false,
      dateSheetTarget: null,
      dateSheetField: null,
      dateSheetLabel: "",
    };
  },
  computed: {
    p() {
      return this.productForm;
    },
    integratorSubTabs() {
      return [
        {
          key: "woocommerce",
          label: this.$t("woocommerce"),
          icon: "mdi-wordpress",
        },
        {
          key: "digikala",
          label: this.$t("digikala"),
          img: require("@/assets/digikala.png"),
        },
        {
          key: "basalam",
          label: this.$t("basalam"),
          img: require("@/assets/basalam.png"),
        },
        {
          key: "markito",
          label: this.$t("markito"),
          img: require("@/assets/markitoLogo.png"),
        },
        {
          key: "portal",
          label: this.$t("portal"),
          img: require("@/assets/portal.png"),
          imgSm: true,
        },
        {
          key: "portal3",
          label: this.$t("portal3"),
          img: require("@/assets/portal.png"),
          imgSm: true,
        },
        {
          key: "sazito",
          label: this.$t("sazito"),
          img: require("@/assets/sazito.png"),
        },
        {
          key: "webzi",
          label: this.$t("webzi"),
          img: require("@/assets/webzi.png"),
        },
        {
          key: "mixin",
          label: this.$t("mixin"),
          img: require("@/assets/integrators/mixin.png"),
        },
        {
          key: "snapshop",
          label: this.$t("snap-shop"),
          img: require("@/assets/integrators/snapShop.png"),
        },
      ];
    },
    shellStyle() {
      const appBar = 52;
      // Mobile bottom nav is dashboard-only.
      const bottomNav = 0;
      const height = `calc(100dvh - ${appBar}px)`;
      return {
        height,
        maxHeight: height,
        // Pull shell under floating footer; white actions pad clears the nav.
        marginBottom: bottomNav ? `-${bottomNav}px` : "0",
        paddingBottom: "0",
        boxSizing: "border-box",
      };
    },
    actionsStyle() {
      const bottomNav = 0;
      // White actions fill under footer; keep submit buttons above the nav + safe area.
      return {
        paddingBottom: bottomNav
          ? `calc(${bottomNav + 10}px + env(safe-area-inset-bottom, 0px))`
          : "calc(8px + env(safe-area-inset-bottom, 0px))",
      };
    },
    unitText() {
      return this.entityLabel(this.p.unit);
    },
    kindText() {
      return this.entityLabel(this.p.kind);
    },
    groupText() {
      return this.entityLabel(this.p.group);
    },
    cat1Text() {
      return this.entityLabel(this.p.cat1);
    },
    cat2Text() {
      return this.entityLabel(this.p.cat2);
    },
    tagText() {
      const tags = Array.isArray(this.p.tag) ? this.p.tag : [];
      if (!tags.length) return "";
      return tags
        .map((t) => (t && (t.text || t.name || t.title)) || "")
        .filter(Boolean)
        .join("، ");
    },
    branchText() {
      return this.entityLabel(this.p.branch);
    },
    showAdvancedSegments() {
      return !this.p.isVariableProduct;
    },
    dateSheetDisplay() {
      if (!this.dateSheetTarget || !this.dateSheetField) return "";
      const v = this.dateSheetTarget[this.dateSheetField];
      return this.hasValue(v) ? String(v) : "";
    },
    dateSheetProxy: {
      get() {
        if (!this.dateSheetTarget || !this.dateSheetField) return null;
        return this.dateSheetTarget[this.dateSheetField];
      },
      set(val) {
        if (!this.dateSheetTarget || !this.dateSheetField) return;
        this.dateSheetTarget[this.dateSheetField] = val;
      },
    },
    stockHint() {
      const n = (this.p.initial_stocks || []).length;
      return n ? String(n) : "—";
    },
    hasCustomFields() {
      return (
        this.$store.state.allConfig &&
        this.$store.state.allConfig.customFieldsSystem === "active"
      );
    },
    sectionHasAddAction() {
      return (
        this.activeSection === "barcodes" ||
        this.activeSection === "units" ||
        this.activeSection === "customFields" ||
        (this.activeSection === "stock" && this.p.initialCheck) ||
        this.activeSection === "allocation"
      );
    },
    sectionAddLabel() {
      if (this.activeSection === "barcodes") return this.$t("addBarcode");
      if (this.activeSection === "units") return this.$t("addSub-unit");
      if (this.activeSection === "customFields") return this.$t("addCustomeField");
      if (this.activeSection === "stock") return this.$t("addInitialStock");
      if (this.activeSection === "allocation") return this.$t("addAllocation");
      return this.$t("add");
    },
    sectionAddHint() {
      if (this.activeSection === "barcodes") {
        return this.$t("otherBarcodesDescrible");
      }
      if (this.activeSection === "units") {
        return this.$t("otherUnitsDescrible");
      }
      if (this.activeSection === "customFields") {
        return this.$t("clickForAddCustomeField");
      }
      if (this.activeSection === "stock") {
        return this.$t("initialStockInOtherEntrepot");
      }
      if (this.activeSection === "allocation") {
        return this.$t("allocationProductDescribe");
      }
      return "";
    },
    investorRows() {
      if (this.p.type !== "product" || this.p.isVariableProduct) return [];
      return this.p.investors || [];
    },
    sectionTitle() {
      const map = {
        tax: this.$t("tax&priceing"),
        barcodes: this.$t("otherBarcodes"),
        grouping: this.$t("grouping,tags"),
        dimensions: this.$t("dimensions"),
        stock: this.$t("initial-stock"),
        units: this.$t("otherUnits"),
        customFields: this.$t("customFields"),
        otherSetting: this.$t("otherSetting"),
        description: this.$t("description"),
        variants: this.$t("variant-product"),
        pricing: `${this.$t("pricing")} ${this.$t("advance")}`,
        integrator: this.$t("Integrator"),
        shop: this.$t("shop"),
        images: this.$t("image-gallery"),
        allocation: this.$t("allocation"),
      };
      return map[this.activeSection] || "";
    },
    numpadDisplay() {
      const raw = String(this.numpadDraft ?? "").trim();
      if (!raw || raw === "NaN" || Number.isNaN(Number(raw))) {
        return "0";
      }
      if (!this.numpadIsAmount || !this.$numberFormat) return raw;
      try {
        const formatted = this.$numberFormat(raw);
        if (
          formatted === null ||
          formatted === undefined ||
          formatted === "" ||
          String(formatted) === "NaN"
        ) {
          return raw;
        }
        return formatted;
      } catch (e) {
        return raw;
      }
    },
  },
  methods: {
    hasValue(v) {
      if (v === null || v === undefined || v === "") return false;
      if (typeof v === "number" && Number.isNaN(v)) return false;
      if (String(v) === "NaN") return false;
      return true;
    },
    entityLabel(entity) {
      if (!entity) return "";
      return entity.name || entity.title || entity.text || "";
    },
    formatMoney(v) {
      if (!this.hasValue(v)) return "—";
      if (this.$numberFormat) {
        try {
          const formatted = this.$numberFormat(v);
          if (formatted != null && String(formatted) !== "NaN") return formatted;
        } catch (e) {
          /* fallthrough */
        }
      }
      return String(v);
    },
    sanitizeNumpadValue(v) {
      if (!this.hasValue(v)) return "";
      const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
      if (Number.isNaN(n)) return "";
      return String(n);
    },
    onStatusChange(val) {
      this.p.status = val ? "active" : "deactive";
    },
    toggleStatus() {
      this.p.status = this.p.status === "active" ? "deactive" : "active";
    },
    toggleVariableProduct() {
      if (this.p.id || this.p.wasVariableProduct) return;
      this.p.isVariableProduct = !this.p.isVariableProduct;
      if (this.p.isVariableProduct) this.mobileTab = "main";
    },
    toggleProductSerial() {
      this.p.has_serial = this.p.has_serial === "active" ? "deactive" : "active";
    },
    onBack() {
      if (this.p.formType) {
        this.p.$emit("close");
        return;
      }
      this.$router.go(-1);
    },
    openSection(key) {
      this.activeSection = key;
      this.sectionSheet = true;
    },
    setIntegratorSubTab(key) {
      this.integratorSubTab = key || "woocommerce";
      this.$nextTick(() => {
        const scroller = this.$el && this.$el.querySelector(".product-mobile__scroll");
        if (scroller) scroller.scrollTop = 0;
      });
    },
    toggleWooCard(index) {
      this.wooOpenIndex = this.wooOpenIndex === index ? -1 : index;
    },
    toggleBasalamCard(index) {
      this.basalamOpenIndex = this.basalamOpenIndex === index ? -1 : index;
    },
    addWooMobile() {
      if (!this.p || typeof this.p.addWoocommerceIdItems !== "function") return;
      this.p.addWoocommerceIdItems();
      this.$nextTick(() => {
        const len = (this.p.woocommerceIdItems && this.p.woocommerceIdItems.length) || 0;
        this.wooOpenIndex = len ? len - 1 : -1;
      });
    },
    addBasalamMobile() {
      if (!this.p || typeof this.p.addBaSalamIdItems !== "function") return;
      this.p.addBaSalamIdItems();
      this.$nextTick(() => {
        const len = (this.p.basalamIdItems && this.p.basalamIdItems.length) || 0;
        this.basalamOpenIndex = len ? len - 1 : -1;
      });
    },
    panelNameLabel(val) {
      if (!val) return "";
      if (typeof val === "string") return val;
      return val.name || val.title || val.text || "";
    },
    wooCardSummary(item) {
      if (!item) return "—";
      const panel = this.panelNameLabel(item.woocommercePanelName);
      const id = item.woocommerceId ? String(item.woocommerceId) : "";
      if (panel && id) return panel + " · " + id;
      return panel || id || "—";
    },
    basalamCardSummary(item) {
      if (!item) return "—";
      const panel = this.panelNameLabel(item.basalamPanelName);
      const id = item.basalamId ? String(item.basalamId) : "";
      if (panel && id) return panel + " · " + id;
      return panel || id || "—";
    },
    setMobileTab(tab) {
      this.mobileTab = tab || "main";
      this.sectionSheet = false;
      if (tab === "integrator") {
        this.integratorSubTab = this.integratorSubTab || "woocommerce";
      }
      this.$nextTick(() => {
        const scroller = this.$el && this.$el.querySelector(".product-mobile__scroll");
        if (scroller) scroller.scrollTop = 0;
      });
    },
    onSectionAdd() {
      if (this.activeSection === "barcodes") this.p.addBarcode();
      else if (this.activeSection === "units") this.p.addUnitRow();
      else if (this.activeSection === "customFields") {
        const ref = this.$refs.refCustomFields;
        if (ref && typeof ref.addRow === "function") ref.addRow();
      } else if (this.activeSection === "stock") this.p.addInitialStock();
      else if (this.activeSection === "allocation") this.p.addAllocationRow();
    },
    openPicker(refName) {
      const ref = this.$refs[refName];
      if (ref && typeof ref.openMobileDialog === "function") {
        ref.openMobileDialog();
      }
    },
    openUnitPicker() {
      this.openPicker("refUnit");
    },
    openKindPicker() {
      this.openPicker("refKind");
    },
    openUnitRowPicker(index) {
      const ref = this.$refs["refSubUnit" + index];
      const comp = Array.isArray(ref) ? ref[0] : ref;
      if (comp && typeof comp.openMobileDialog === "function") {
        comp.openMobileDialog();
      }
    },
    openInvestorRowPicker(index) {
      const ref = this.$refs["refInvestor" + index];
      const comp = Array.isArray(ref) ? ref[0] : ref;
      if (comp && typeof comp.openMobileDialog === "function") {
        comp.openMobileDialog();
      }
    },
    openDateSheet(target, field, label) {
      this.dateSheetTarget = target;
      this.dateSheetField = field;
      this.dateSheetLabel = label || this.$t("date");
      this.dateSheet = true;
    },
    clearDateSheetValue() {
      if (!this.dateSheetTarget || !this.dateSheetField) return;
      this.dateSheetTarget[this.dateSheetField] = null;
    },
    onUnitDefaultChange(item, index, val) {
      item.default = val ? "active" : null;
      if (typeof this.p.clearDefaultUnits === "function") {
        this.p.clearDefaultUnits(item, index);
      }
    },
    openTextSheet(field, label) {
      this.textSheetField = field;
      this.textSheetLabel = label;
      this.textSheetDraft = this.p[field] != null ? String(this.p[field]) : "";
      this.textSheet = true;
    },
    confirmTextSheet() {
      if (this.textSheetField) {
        this.p[this.textSheetField] = this.textSheetDraft;
        if (this.textSheetField === "code" && typeof this.p.checkCode === "function") {
          this.p.checkCode();
        }
      }
      this.textSheet = false;
    },
    openNumpad(field, label, isAmount, target = null) {
      this.numpadField = field;
      this.numpadTarget = target || null;
      this.numpadLabel = label;
      this.numpadIsAmount = !!isAmount;
      const source = target || this.p;
      this.numpadDraft = this.sanitizeNumpadValue(source[field]);
      this.numpadOpen = true;
    },
    numpadDigit(num) {
      this.numpadAppend(String(num));
    },
    numpadAppend(chunk) {
      const piece = String(chunk);
      if (!piece) return;
      if (String(this.numpadDraft).length + piece.length > 16) return;
      if (!this.numpadDraft && /^\d+$/.test(piece)) {
        this.numpadDraft = piece.replace(/^0+(?=\d)/, "");
      } else if (this.numpadDraft === "0" && /^\d+$/.test(piece)) {
        this.numpadDraft = piece;
      } else {
        this.numpadDraft = String(this.numpadDraft) + piece;
      }
    },
    numpadDot() {
      if (!this.numpadIsAmount) return;
      if (!this.numpadDraft) {
        this.numpadDraft = "0.";
        return;
      }
      if (String(this.numpadDraft).includes(".")) return;
      this.numpadDraft = String(this.numpadDraft) + ".";
    },
    numpadBackspace() {
      const current = String(this.numpadDraft || "");
      this.numpadDraft = current.length <= 1 ? "" : current.slice(0, -1);
    },
    numpadClear() {
      this.numpadDraft = "";
    },
    numpadStep(delta) {
      const next = (parseFloat(this.numpadDraft) || 0) + delta;
      this.numpadDraft = next < 0 ? "0" : String(next);
    },
    confirmNumpad() {
      if (this.numpadField) {
        const target = this.numpadTarget || this.p;
        const raw = String(this.numpadDraft || "").trim();
        if (raw === "") {
          target[this.numpadField] = null;
        } else {
          const parsed = parseFloat(raw);
          target[this.numpadField] = Number.isNaN(parsed) ? null : parsed;
        }
        if (!this.numpadTarget) {
          if (
            (this.numpadField === "price" || this.numpadField === "taxPercent") &&
            typeof this.p.calcPriceWithTax === "function"
          ) {
            this.p.calcPriceWithTax();
          } else if (
            this.numpadField === "priceWithTax" &&
            typeof this.p.calcPrice === "function"
          ) {
            this.p.calcPrice();
          }
        }
      }
      this.numpadField = null;
      this.numpadTarget = null;
      this.numpadOpen = false;
    },
    syncRefsToParent() {
      const parentRefs = this.p.$refs;
      if (!parentRefs) return;
      if (this.$refs.form) parentRefs.form = this.$refs.form;
      if (this.$refs.refFilePicker) parentRefs.refFilePicker = this.$refs.refFilePicker;
      if (this.$refs.refUnit) parentRefs.refUnit = this.$refs.refUnit;
      if (this.$refs.refProductImages) {
        parentRefs.refProductImages = this.$refs.refProductImages;
      }
      if (this.$refs.refGroup) parentRefs.refGroup = this.$refs.refGroup;
      if (this.$refs.refCat1) parentRefs.refCat1 = this.$refs.refCat1;
      if (this.$refs.refCat2) parentRefs.refCat2 = this.$refs.refCat2;
    },
    submit(submitOnly) {
      if (this.$refs.form && !this.$refs.form.validate()) return;
      this.syncRefsToParent();
      this.p.create(submitOnly);
    },
    submitUpdate() {
      if (this.$refs.form && !this.$refs.form.validate()) return;
      this.syncRefsToParent();
      this.p.update();
    },
  },
};
</script>

<style scoped>
.product-mobile {
  display: flex;
  flex-direction: column;
  background: #f3f5f7;
  overflow: hidden;
  width: 100%;
}
.product-mobile-header {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  gap: 8px;
  min-height: 52px;
  padding: 8px 12px;
  background: #fff;
  border-bottom: 1px solid #e8eef2;
  direction: ltr; /* physical slots: back left, list right */
}
.product-mobile-header__title {
  margin: 0;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: #37474f;
  direction: rtl;
  grid-column: 2;
}
.product-mobile-header__icon {
  width: 40px;
  height: 40px;
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.product-mobile-header__back {
  grid-column: 1; /* left */
}
.product-mobile-header__list {
  grid-column: 3; /* right */
}
.product-mobile__form {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.product-mobile__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 12px 12px 14px;
}
.product-mobile-toolbar {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
  min-height: 36px;
}
.product-mobile-type {
  display: flex;
  flex: 0 0 auto;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #dbe3ea;
}
.product-mobile-type--compact {
  max-width: 46%;
}
.product-mobile-type__item {
  flex: 1 1 auto;
  border: 0;
  background: transparent;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 700;
  color: #78909c;
  cursor: pointer;
  white-space: nowrap;
  line-height: 1.2;
}
.product-mobile-type__item--active {
  background: #5b7c99;
  color: #fff;
}
.product-mobile-card {
  background: #fff;
  border-radius: 12px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(55, 71, 79, 0.06);
}
.product-mobile-hero {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.product-mobile-hero__body {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fff;
}
.product-mobile-hero__row {
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: stretch;
  min-height: 88px;
}
.product-mobile-hero__main {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 88px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
}
.product-mobile-hero__avatar {
  flex: 0 0 88px;
  width: 88px;
  min-width: 88px;
  height: 88px;
  display: flex;
}
.product-mobile-hero__avatar ::v-deep .v-card {
  background: transparent !important;
  box-shadow: none !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
}
.product-mobile-hero__avatar ::v-deep .v-avatar {
  border-radius: 8px !important;
  width: 88px !important;
  min-width: 88px !important;
  height: 88px !important;
  max-height: 100%;
  overflow: hidden;
}
.product-mobile-hero__avatar ::v-deep .v-avatar .v-image,
.product-mobile-hero__avatar ::v-deep .v-avatar img {
  border-radius: 8px !important;
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
}
.product-mobile-hero__name {
  margin: 0 !important;
  padding: 0 !important;
  flex: 0 0 auto;
}
.product-mobile-hero__name ::v-deep .v-input__control,
.product-mobile-hero__name ::v-deep .v-input__slot {
  margin-bottom: 0 !important;
}
.product-mobile-hero__name ::v-deep .v-text-field__details {
  margin: 0 !important;
  min-height: 0 !important;
  padding: 0 !important;
}
.product-mobile-hero__status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 36px;
  padding-top: 4px;
  border-top: 1px solid #eceff1;
  gap: 12px;
}
.product-mobile-hero__status-label {
  font-size: 13px;
  color: #546e7a;
  font-weight: 600;
  line-height: 1.2;
}
.product-mobile-hero__status {
  margin: 0 !important;
  padding: 0 !important;
  flex: 0 0 auto;
}
.product-mobile-hero__status ::v-deep .v-input__slot {
  margin-bottom: 0 !important;
}
.product-mobile-hero__code-field {
  width: 100%;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 0;
  padding: 8px 10px;
  border: 1px dashed #b0bec5;
  border-radius: 8px;
  background: #f7f9fc;
  cursor: pointer;
  text-align: right;
}
.product-mobile-hero__code-field:active {
  background: #eef3f7;
}
.product-mobile-hero__code-label {
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  white-space: nowrap;
}
.product-mobile-hero__code-label::after {
  content: " :";
}
.product-mobile-hero__code-value {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.product-mobile-hero__code-value--empty {
  color: #b0bec5;
  font-weight: 500;
}
.product-mobile-flags {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-end;
  gap: 6px;
  min-width: 0;
  flex: 1 1 auto;
}
.product-mobile-flag {
  border: 1px solid #c5d0da;
  background: #fff;
  color: #607d8b;
  border-radius: 8px;
  padding: 7px 9px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  line-height: 1.2;
  white-space: nowrap;
}
.product-mobile-flag--on {
  border-color: #5b7c99;
  background: #dde6ee;
  color: #37474f;
}
.product-mobile-flag:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.product-mobile-summary {
  padding: 4px 12px 0;
  overflow: hidden;
}
.product-mobile-advanced {
  margin: 0 0 10px;
  padding: 0 2px;
}
.product-mobile-advanced--top {
  flex: 0 0 auto;
  margin: 0;
  padding: 8px 12px 10px;
  background: #f3f5f7;
  border-bottom: 1px solid #e8eef2;
  z-index: 2;
}
.product-mobile-advanced__chips {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 2px 2px;
  scrollbar-width: none;
}
.product-mobile-advanced__chips::-webkit-scrollbar {
  display: none;
}
.product-mobile-advanced__chip {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 8px 12px;
  border: 1px solid #c5d0e0;
  border-radius: 999px;
  background: #eef3f8;
  color: #455a64;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
}
.product-mobile-advanced__chip:active {
  background: #e0e7f0;
}
.product-mobile-advanced__chip--on {
  border-color: #5b7c99;
  background: #5b7c99;
  color: #fff;
}
.product-mobile-tab-panel {
  padding: 4px 2px 12px;
}
.product-mobile-integ__chips {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding: 2px 2px 12px;
  margin-bottom: 4px;
}
.product-mobile-integ__chips::-webkit-scrollbar {
  display: none;
}
.product-mobile-integ__chip {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 6px 12px;
  border: 1px solid #c5d0da;
  border-radius: 999px;
  background: #fff;
  color: #5b7c99;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
}
.product-mobile-integ__chip:active {
  background: #f0f4f8;
}
.product-mobile-integ__chip--on {
  border-color: #5b7c99;
  background: #5b7c99;
  color: #fff;
}
.product-mobile-integ__chip-logo {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex: 0 0 auto;
  display: block;
}
.product-mobile-integ__chip-logo--sm {
  width: 10px;
  height: 16px;
}
.product-mobile-integ__panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.product-mobile-integ__add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 44px;
  border: 1px solid #c5d0e0;
  border-radius: 10px;
  background: #e8edf5;
  color: #5a6b85;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.product-mobile-integ__add:active {
  background: #dde4ef;
}
.product-mobile-integ__add:disabled {
  opacity: 0.6;
  cursor: default;
}
.product-mobile-integ__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 28px 16px;
  text-align: center;
  border: 1px dashed #d5dde5;
  border-radius: 12px;
  background: #f7f9fc;
}
.product-mobile-integ__empty-title {
  font-size: 14px;
  font-weight: 800;
  color: #546e7a;
  line-height: 1.4;
}
.product-mobile-integ__empty-hint {
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
  line-height: 1.5;
}
.product-mobile-integ__card {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 10px 12px;
  border: 1px solid #e0e4e8;
  border-radius: 12px;
  background: #fff;
}
.product-mobile-integ__card--open {
  padding-bottom: 12px;
}
.product-mobile-integ__card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}
.product-mobile-integ__card--open .product-mobile-integ__card-top {
  padding-bottom: 10px;
  margin-bottom: 12px;
  border-bottom: 1px solid #eef2f5;
}
.product-mobile-integ__card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1 1 auto;
}
.product-mobile-integ__card-titles {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.product-mobile-integ__card-title {
  font-size: 12px;
  font-weight: 700;
  color: #78909c;
  line-height: 1.3;
}
.product-mobile-integ__card-summary {
  font-size: 14px;
  font-weight: 700;
  color: #37474f;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.product-mobile-integ__card-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.product-mobile-integ__simple {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 12px;
  border: 1px solid #e0e4e8;
  border-radius: 12px;
  background: #fff;
}
.product-mobile-integ__input {
  width: 100%;
  min-height: 40px;
  padding: 0 10px;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  background: #fff;
  color: #37474f;
  font-size: 15px;
  font-weight: 700;
  text-align: right;
  box-sizing: border-box;
  outline: none;
}
.product-mobile-integ__input:focus {
  border-color: #90caf9;
  background: #e3f2fd;
}
.product-mobile-integ__picker {
  display: block;
  width: 100%;
  margin: 0 !important;
  padding: 0 !important;
  flex: 0 0 40px;
  height: 40px !important;
  max-height: 40px !important;
  overflow: hidden !important;
}
.product-mobile-integ__picker ::v-deep .v-input,
.product-mobile-integ__picker ::v-deep .v-text-field,
.product-mobile-integ__picker ::v-deep .v-autocomplete {
  margin: 0 !important;
  margin-top: 0 !important;
  padding: 0 !important;
  padding-top: 0 !important;
}
.product-mobile-integ__picker ::v-deep .v-input__control {
  min-height: 40px !important;
  height: 40px !important;
}
.product-mobile-integ__picker ::v-deep .v-input__slot {
  min-height: 40px !important;
  height: 40px !important;
  margin: 0 !important;
  padding: 0 10px !important;
  border: 1px solid #cfd8dc !important;
  border-radius: 8px !important;
  background: #fff !important;
  box-shadow: none !important;
}
.product-mobile-integ__picker ::v-deep fieldset {
  display: none !important;
}
.product-mobile-integ__picker ::v-deep .v-input__slot::before,
.product-mobile-integ__picker ::v-deep .v-input__slot::after {
  display: none !important;
}
.product-mobile-integ__picker ::v-deep .v-label {
  display: none !important;
}
.product-mobile-integ__picker ::v-deep .v-text-field__details,
.product-mobile-integ__picker ::v-deep .v-messages {
  display: none !important;
  min-height: 0 !important;
  height: 0 !important;
}
.product-mobile-integ__picker ::v-deep .v-input__append-inner,
.product-mobile-integ__picker ::v-deep .v-input__prepend-inner {
  margin-top: 0 !important;
  align-self: center !important;
}
.product-mobile-integ__embed ::v-deep .container,
.product-mobile-integ__embed ::v-deep .v-container {
  padding-left: 0 !important;
  padding-right: 0 !important;
  max-width: 100% !important;
}
.product-mobile-integ__embed ::v-deep .wrapper {
  padding: 0 !important;
}
.product-mobile-integ__embed ::v-deep .column-panel {
  border-radius: 12px !important;
  border: 1px solid #e0e4e8 !important;
  background: #fff !important;
}
.product-mobile-field {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
  padding: 6px 4px;
  border-bottom: 1px solid #e8eef2;
}
.product-mobile-field--select {
  cursor: pointer;
}
.product-mobile-field--select:active {
  background: #f7f9fc;
}
.product-mobile-field--area {
  align-items: flex-start;
  min-height: 120px;
  padding: 10px 4px 12px;
  flex-direction: column;
  gap: 8px;
  cursor: text;
  border-bottom: 0;
}
.product-mobile-field--area .product-mobile-field__label {
  max-width: none;
  padding: 0 2px;
}
.product-mobile-field__label {
  flex: 0 0 auto;
  max-width: 38%;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.3;
  text-align: right;
}
.product-mobile-field__control {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  border: 0;
  background: transparent;
  padding: 6px 0;
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
  text-align: right;
  line-height: 1.3;
}
.product-mobile-field__control--select {
  cursor: pointer;
}
.product-mobile-field__input {
  width: 100%;
  outline: none;
  cursor: text;
  font-family: inherit;
  -moz-appearance: textfield;
}
.product-mobile-field__input::-webkit-outer-spin-button,
.product-mobile-field__input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.product-mobile-field__input::placeholder {
  color: #b0bec5;
  font-weight: 500;
}
.product-mobile-field__placeholder {
  color: #b0bec5;
  font-weight: 500;
}
.product-mobile-field__textarea {
  width: 100%;
  min-height: 110px;
  resize: vertical;
  border: 1px solid #dbe3ea;
  border-radius: 10px;
  outline: none;
  background: #f7f9fc;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: #263238;
  text-align: right;
  line-height: 1.5;
  padding: 12px;
  box-sizing: border-box;
  cursor: text;
}
.product-mobile-field__textarea:focus {
  border-color: #5b7c99;
  background: #fff;
}
.product-mobile-field__textarea::placeholder {
  color: #b0bec5;
  font-weight: 500;
}
.product-mobile-summary__more {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 12px 8px;
  border: 0;
  border-top: 1px solid #eef2f5;
  background: transparent;
  color: #5b7c99;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.product-mobile-summary__more:active {
  background: #f7f9fc;
}
.product-mobile-picker-host {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}
.product-mobile-sections__row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 14px;
  border: 0;
  border-bottom: 1px solid #eef2f5;
  background: transparent;
  text-align: right;
  cursor: pointer;
}
.product-mobile-sections__row:last-child {
  border-bottom: 0;
}
.product-mobile-sections__row:active {
  background: #f7f9fc;
}
.product-mobile-sections__icon {
  flex: 0 0 auto;
}
.product-mobile-sections__copy {
  flex: 1 1 auto;
  min-width: 0;
}
.product-mobile-sections__title {
  font-size: 14px;
  font-weight: 700;
  color: #37474f;
}
.product-mobile-sections__hint {
  margin-top: 3px;
  font-size: 12px;
  color: #90a4ae;
  line-height: 1.35;
}
.product-mobile-actions {
  flex: 0 0 auto;
  padding-top: 10px;
  padding-left: 12px;
  padding-right: 12px;
  background: #fff;
  border-top: 1px solid #e0e6ed;
  z-index: 3;
}
.product-mobile-actions__row {
  display: flex;
  gap: 10px;
}
.product-mobile-actions__primary {
  flex: 1 1 0;
  background: #5b7c99 !important;
  color: #fff !important;
  border-radius: 10px !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
  box-shadow: none !important;
}
.product-mobile-actions__secondary {
  flex: 1 1 0;
  border-radius: 10px !important;
  border-color: #cfd8dc !important;
  color: #5b7c99 !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
}
.product-mobile-section-sheet {
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  overflow: hidden;
}
.product-mobile-section-sheet__toolbar {
  flex: 0 0 auto;
  padding: 0 16px 12px;
  background: #fff;
}
.product-mobile-section-sheet__toolbar--composer {
  border-bottom: 1px solid #eef2f5;
}
.product-mobile-section-sheet__hint {
  margin: 0 0 12px;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.5;
  text-align: right;
}
.product-mobile-section-sheet__add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 44px;
  border: 1px solid #c5d0e0;
  border-radius: 10px;
  background: #e8edf5;
  color: #5a6b85;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.product-mobile-section-sheet__add:active {
  background: #dde4ef;
}
.product-mobile-section-sheet__empty {
  margin-bottom: 4px;
  padding: 16px;
  border: 1px dashed #cfd8dc;
  border-radius: 12px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #90a4ae;
}
.product-mobile-section-sheet__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
}
.product-mobile-sheet-fields {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.product-mobile-sheet-fields .product-mobile-field {
  min-height: 52px;
  padding: 10px 2px;
}
.product-mobile-sheet-fields .product-mobile-field:last-child {
  border-bottom: 0;
}
.product-mobile-sheet-fields .product-mobile-field__label {
  max-width: 46%;
}
.product-mobile-field--switch {
  justify-content: space-between;
}
.product-mobile-field__switch {
  margin: 0 !important;
  padding: 0 !important;
  flex: 0 0 auto;
}
.product-mobile-field__switch ::v-deep .v-input__slot {
  margin-bottom: 0 !important;
}
.product-mobile-section-sheet__footer {
  flex: 0 0 auto;
  padding: 10px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid #eef2f5;
}
.product-mobile-section-sheet__save {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  border: 0;
  border-radius: 10px;
  background: #5b7c99;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}
.product-mobile-section-sheet__save:active {
  background: #4f6d88;
}
.product-mobile-section-sheet__body {
  max-height: calc(88vh - 72px);
  overflow-y: auto;
}
.product-mobile-inline-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.product-mobile-stock {
  padding: 10px;
  border: 1px solid #e8eef2;
  border-radius: 10px;
}
.product-mobile-composer-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid #e0e4e8;
  border-radius: 12px;
  background: #fff;
}
.product-mobile-composer-card:last-child {
  margin-bottom: 0;
}
.product-mobile-composer-card__head {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.product-mobile-composer-card__main {
  flex: 1 1 auto;
  min-width: 0;
}
.product-mobile-composer-card__remove {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #f8bbd0;
  border-radius: 10px;
  background: #fce4ec;
  cursor: pointer;
}
.product-mobile-composer-field__label-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0 2px 6px;
}
.product-mobile-composer-field__label-row .product-mobile-composer-field__label {
  margin: 0;
}
.product-mobile-composer-card__ratio {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef3f8;
  color: #5a6b85;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.4;
  white-space: nowrap;
}
.product-mobile-composer-card__switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 40px;
  padding-top: 2px;
}
.product-mobile-composer-card__picker {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}
.product-mobile-composer-field {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
}
.product-mobile-composer-field__label {
  display: block;
  margin: 0 2px 6px;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  line-height: 1.4;
  text-align: right;
}
.product-mobile-composer-field__control {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-height: 40px;
  padding: 0 10px;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  background: #fff;
  color: #37474f;
  font-size: 15px;
  font-weight: 700;
  text-align: right;
  cursor: pointer;
  box-sizing: border-box;
}
.product-mobile-composer-field__control--numpad {
  justify-content: center;
  text-align: center;
}
.product-mobile-composer-field__control:active {
  background: #e3f2fd;
  border-color: #90caf9;
}
.product-mobile-sheet-fields > .product-mobile-composer-field--tight {
  margin-top: 4px;
}
.product-mobile-composer-field--tight .product-mobile-composer-field__label {
  margin-bottom: 4px;
}
.product-mobile-field--divider-top {
  margin-top: 8px;
  border-top: 1px solid #e8eef2;
  padding-top: 10px;
}
.product-mobile-date {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.product-mobile-date__selected {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #e0e6ed;
  border-radius: 12px;
  background: #f7f9fc;
}
.product-mobile-date__label {
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.product-mobile-date__value {
  font-size: 18px;
  font-weight: 800;
  color: #263238;
}
.product-mobile-date__clear {
  border: 0;
  background: transparent;
  color: #e91e63;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 0;
  cursor: pointer;
}
.product-mobile-date__picker {
  width: 100%;
}
.product-mobile-date__picker ::v-deep .vpd-main,
.product-mobile-date__picker ::v-deep .vpd-wrapper,
.product-mobile-date__picker ::v-deep .vpd-container,
.product-mobile-date__picker ::v-deep .vpd-content {
  width: 100% !important;
  max-width: 100% !important;
  box-shadow: none !important;
  border: 1px solid #e0e6ed !important;
  border-radius: 12px !important;
  background: #fff !important;
  overflow: hidden;
}
.product-mobile-select-control {
  flex: none !important;
  width: 100%;
  height: 40px !important;
  max-height: 40px !important;
  min-height: 40px !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}
.product-mobile-select-control.v-input {
  margin: 0 !important;
  min-height: 40px !important;
  height: 40px !important;
  max-height: 40px !important;
}
.product-mobile-select-control ::v-deep .v-input__control {
  min-height: 40px !important;
  height: 40px !important;
  max-height: 40px !important;
  margin: 0 !important;
}
.product-mobile-select-control ::v-deep .v-input__slot {
  min-height: 40px !important;
  height: 40px !important;
  max-height: 40px !important;
  margin: 0 !important;
  padding: 0 12px !important;
  border: 1px solid #cfd8dc !important;
  border-radius: 8px !important;
  background: #fff !important;
  box-shadow: none !important;
}
.product-mobile-select-control ::v-deep .v-input__slot::before,
.product-mobile-select-control ::v-deep .v-input__slot::after {
  display: none !important;
}
.product-mobile-select-control ::v-deep .v-input__append-inner,
.product-mobile-select-control ::v-deep .v-input__prepend-inner {
  margin-top: 0 !important;
  align-self: center !important;
}
.product-mobile-select-control ::v-deep .v-select__selections {
  flex-wrap: nowrap;
  overflow: hidden;
  min-height: 0 !important;
  padding: 0 !important;
}
.product-mobile-select-control ::v-deep .v-select__selection {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0 !important;
  font-size: 15px;
  font-weight: 700;
  color: #37474f;
}
.product-mobile-other-link {
  width: 100%;
  display: block;
  text-align: right;
  border: 1px solid #e8eef2;
  background: #f7f9fc;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #455a64;
  cursor: pointer;
}
</style>
